import React, { useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  Button,
  useTheme,
  Stack,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  SelectChangeEvent,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { BaseLabel, LabelType, LabelCategory, labelsService } from '../../services/labels-service';
import { config } from '../../config/config';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`label-tabpanel-${index}`}
      aria-labelledby={`label-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

interface AddLabelPanelProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
}

const LABEL_TYPES: LabelType[] = ['BUTTON', 'TITLE', 'MESSAGE', 'ERROR', 'ANY', 'FOOTNOTE', 'TOOLTIP'];
const LABEL_CATEGORIES: LabelCategory[] = ['COLLECTION_POINT', 'EMAIL', 'GENERAL'];

const AddLabelPanel: React.FC<AddLabelPanelProps> = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(0);
  const [label, setLabel] = useState<Partial<BaseLabel>>({
    LabelKey: '',
    LabelText: '',
    LabelType: 'ANY',
    Category: 'GENERAL',
    Description: '',
  });
  const [error, setError] = useState<string | null>(null);

  const [errors, setErrors] = useState<{
    LabelKey?: string;
    LabelText?: string;
    LabelType?: string;
    Category?: string;
  }>({});

  const validateField = (field: keyof BaseLabel, value: any): string | undefined => {
    switch (field) {
      case 'LabelKey':
        if (!value?.trim()) {
          return 'Label Key is required';
        }
        if (value.trim().length < 3) {
          return 'Label Key must be at least 3 characters';
        }
        // Check for spaces
        if (value.includes(' ')) {
          return 'Label Key cannot contain spaces';
        }
        // Check for unsafe characters - only allow alphanumeric, underscore, hyphen, and dot
        if (!/^[a-zA-Z0-9_.-]+$/.test(value)) {
          return 'Label Key can only contain letters, numbers, underscore (_), hyphen (-), and dot (.)';
        }
        break;
      case 'LabelText':
        if (!value?.trim()) {
          return 'Label Text is required';
        }
        if (value.trim().length < 3) {
          return 'Label Text must be at least 3 characters';
        }
        break;
      case 'LabelType':
        if (!value) {
          return 'Label Type is required';
        }
        break;
      case 'Category':
        if (!value) {
          return 'Category is required';
        }
        break;
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    let isValid = true;

    // Validate all fields
    const fieldsToValidate: (keyof BaseLabel)[] = ['LabelKey', 'LabelText', 'LabelType', 'Category'];
    fieldsToValidate.forEach(field => {
      const error = validateField(field, label[field]);
      if (error) {
        newErrors[field as keyof typeof errors] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleBlur = (field: keyof BaseLabel) => () => {
    const error = validateField(field, label[field]);
    setErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleTextChange = (field: keyof BaseLabel) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLabel(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleSelectChange = (field: keyof BaseLabel) => (
    event: SelectChangeEvent
  ) => {
    setLabel(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear error when user makes a selection
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const resetForm = () => {
    setLabel({
      LabelKey: '',
      LabelText: '',
      LabelType: 'ANY',
      Category: 'GENERAL',
      Description: '',
    });
    setErrors({});
    setCurrentTab(0);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await labelsService.createBaseLabel({
        ...label,
        createdBy: 1, // TODO: Get from auth context
      } as Omit<BaseLabel, 'labelId' | 'createdDate'>);
      resetForm();
      onClose();
      onSave?.();
    } catch (error: any) {
      // Log the complete error response from server
      console.error('Server error response:', error.response?.data || error);
      // Display the specific error message from the server response
      setError(error.response?.data?.message || 'Failed to create label');
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: '600px',
          bgcolor: theme.palette.background.paper,
          borderLeft: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}>
          <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
            Add New Label
          </Typography>
          <IconButton 
            onClick={handleClose} 
            sx={{ 
              color: theme.palette.text.secondary,
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                },
              },
              '& .MuiTabs-indicator': {
                bgcolor: theme.palette.primary.main,
              },
            }}
          >
            <Tab label="Details" />
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
        }}>
          <TabPanel value={currentTab} index={0}>
            <Stack spacing={3}>
              <TextField
                label="Label Key"
                required
                fullWidth
                size="small"
                value={label.LabelKey}
                onChange={handleTextChange('LabelKey')}
                onBlur={handleBlur('LabelKey')}
                error={!!errors.LabelKey}
                helperText={errors.LabelKey || "Specify a unique key for the label. Will be of aid when referring to the label"}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                  },
                  '& .MuiInputBase-input': {
                    color: theme.palette.text.primary,
                  },
                  '& .MuiFormHelperText-root': {
                    color: errors.LabelKey ? theme.palette.error.main : theme.palette.text.secondary,
                  },
                }}
              />

              <FormControl fullWidth size="small" error={!!errors.LabelType}>
                <InputLabel sx={{ color: theme.palette.text.secondary }}>Label Type</InputLabel>
                <Select
                  required
                  value={label.LabelType}
                  onChange={handleSelectChange('LabelType')}
                  onBlur={handleBlur('LabelType')}
                  label="Label Type"
                  sx={{
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: errors.LabelType ? theme.palette.error.main : theme.palette.divider,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '& .MuiSelect-icon': {
                      color: theme.palette.text.secondary,
                    },
                  }}
                >
                  {LABEL_TYPES.map((type) => (
                    <MenuItem key={type} value={type} sx={{ color: theme.palette.text.primary }}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
                {errors.LabelType && (
                  <Typography variant="caption" sx={{ color: theme.palette.error.main, mt: 1 }}>
                    {errors.LabelType}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth size="small" error={!!errors.Category}>
                <InputLabel sx={{ color: theme.palette.text.secondary }}>Label Category</InputLabel>
                <Select
                  required
                  value={label.Category}
                  onChange={handleSelectChange('Category')}
                  onBlur={handleBlur('Category')}
                  label="Label Category"
                  sx={{
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: errors.Category ? theme.palette.error.main : theme.palette.divider,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '& .MuiSelect-icon': {
                      color: theme.palette.text.secondary,
                    },
                  }}
                >
                  {LABEL_CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category} sx={{ color: theme.palette.text.primary }}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
                {errors.Category && (
                  <Typography variant="caption" sx={{ color: theme.palette.error.main, mt: 1 }}>
                    {errors.Category}
                  </Typography>
                )}
              </FormControl>

              <TextField
                label="Label Text"
                required
                fullWidth
                multiline
                rows={2}
                size="small"
                value={label.LabelText}
                onChange={handleTextChange('LabelText')}
                onBlur={handleBlur('LabelText')}
                error={!!errors.LabelText}
                helperText={errors.LabelText}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: errors.LabelText ? theme.palette.error.main : theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                  },
                  '& .MuiInputBase-input': {
                    color: theme.palette.text.primary,
                  },
                  '& .MuiFormHelperText-root': {
                    color: errors.LabelText ? theme.palette.error.main : theme.palette.text.secondary,
                  },
                }}
              />

              <TextField
                label="Description"
                fullWidth
                multiline
                rows={4}
                size="small"
                value={label.Description}
                onChange={handleTextChange('Description')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                  },
                  '& .MuiInputBase-input': {
                    color: theme.palette.text.primary,
                  },
                }}
              />
            </Stack>
          </TabPanel>
        </Box>

        {/* Footer */}
        <Box sx={{
          p: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
        }}>
          <Button
            onClick={handleClose}
            sx={{ 
              color: theme.palette.text.primary,
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            Save
          </Button>
        </Box>

        {/* Add Snackbar for error messages */}
        <Snackbar 
          open={!!error} 
          autoHideDuration={config.ui.snackbar.autoHideDuration}
          onClose={() => setError(null)}
          anchorOrigin={config.ui.snackbar.anchorOrigin}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </Drawer>
  );
};

export default AddLabelPanel; 